import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, of, from } from 'rxjs';
import { tap, switchMap, catchError } from 'rxjs/operators';
import { PipelineEnvironment } from '@telespot/domain';
import { PIPELINE_SERVICE, type PipelineRepository } from '@telespot/protocols/domain';

export interface Pipeline {
  id: string;
  names: Record<string, string>;
  environment: PipelineEnvironment;
}

export interface PipelineState {
  pipelines: Pipeline[];
  loading: boolean;
  error: string | undefined;
}

const initialState: PipelineState = {
  pipelines: [],
  loading: true,
  error: undefined,
};

@Injectable()
export class PipelineStore extends ComponentStore<PipelineState> {
  private readonly pipelineRepo = inject<PipelineRepository>(PIPELINE_SERVICE);

  constructor() {
    super(initialState);
  }

  // #region Selectors

  public readonly pipelines$ = this.select((state) => state.pipelines);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly error$ = this.select((state) => state.error);

  // #endregion

  // #region Updaters

  public readonly setPipelines = this.updater((state, pipelines: Pipeline[]) => ({
    ...state,
    pipelines,
  }));

  public readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  public readonly setError = this.updater((state, error: string) => ({
    ...state,
    error,
  }));

  public readonly clearError = this.updater((state) => ({
    ...state,
    error: undefined,
  }));

  // #endregion

  // #region Effects

  public readonly findForSample = this.effect((sampleId$: Observable<string>) =>
    sampleId$.pipe(
      tap(() => {
        this.setLoading(true);
        this.clearError();
      }),
      switchMap((sampleId: string) => {
        return from(this.pipelineRepo.findForSample(sampleId)).pipe(
          tap((pipelines: Pipeline[]) => {
            this.setPipelines(pipelines);
            this.setLoading(false);
            this.clearError();
          }),
          catchError((error: string) => {
            this.setError(error);
            this.setLoading(false);
            return of(null);
          })
        );
      })
    )
  );

  // #endregion
}
